import React, { useEffect, useState } from "react";
import { Play, ArrowRight } from "lucide-react";
import { Link } from "../lib/LocalizedLink";
import Reveal from "./Reveal";
import { factory, brand } from "../mock/content";
import { getActiveLang } from "../lib/activeLang";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "./ui/carousel";
import LazyVideo from "./LazyVideo";
const FT = {
  it: { badge: "Documentario integrale · 10 min", ceo: "Con il CEO Michele Torri", watch: "Guarda il documentario", phase: "Fasi della produzione", short: "Clip" },
  en: { badge: "Full documentary · 10 min", ceo: "With CEO Michele Torri", watch: "Watch the documentary", phase: "Production stages", short: "Clip" },
  de: { badge: "Vollständige Doku · 10 Min.", ceo: "Mit CEO Michele Torri", watch: "Dokumentation ansehen", phase: "Produktionsschritte", short: "Clip" },
  fr: { badge: "Documentaire intégral · 10 min", ceo: "Avec le PDG Michele Torri", watch: "Voir le documentaire", phase: "Étapes de production", short: "Clip" },
};
const FRAME_THUMBS = ["d_KYfZO98Jo", "4LbdcvL5i2o", "E1lxVGhCe68", "9W_A4vRCA0I"];
const ytThumb = (id) => (FRAME_THUMBS.includes(id) ? `/video-thumbs/${id}.jpg?v=3` : `https://img.youtube.com/vi/${id}/maxresdefault.jpg`);

const FeaturedDoc = ({ v, onPlay, L }) => (
  <button onClick={() => onPlay(v)} className="group relative block w-full text-left overflow-hidden bg-black mb-12" data-testid="factory-featured-doc">
    <span className="relative block w-full aspect-[4/5] sm:aspect-[16/9] md:aspect-[21/9]">
      <img loading="lazy" decoding="async" src={ytThumb(v.id)} onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${v.id}/hqdefault.jpg`; }} alt={v.title} className="absolute inset-0 w-full h-full object-cover object-[center_25%] opacity-80 group-hover:scale-[1.03] transition-transform duration-700" />
      <span className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      <span className="absolute inset-0 flex items-start pt-[28%] sm:pt-0 sm:items-center justify-center">
        <span className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full shadow-2xl group-hover:scale-110 transition-transform" style={{ background: brand.yellow }}>
          <Play size={28} fill="black" className="text-black ml-1" />
        </span>
      </span>
      <span className="absolute left-0 right-0 bottom-0 p-5 md:p-10">
        <span className="flex flex-wrap gap-2">
          <span className="px-2.5 py-1 text-[11px] md:text-[11px] uppercase tracking-[0.18em] font-bold text-black" style={{ fontFamily: "Space Grotesk", background: brand.yellow }}>{L.badge}</span>
          <span className="px-2.5 py-1 text-[11px] md:text-[11px] uppercase tracking-[0.18em] font-bold text-white border border-white/40" style={{ fontFamily: "Space Grotesk" }}>{L.ceo}</span>
        </span>
        <span className="mt-3 block font-black text-white text-2xl md:text-4xl leading-tight" style={{ fontFamily: "Archivo" }}>{v.title}</span>
        <span className="mt-2 hidden md:block text-white/75 max-w-2xl" style={{ fontFamily: "Inter" }}>{v.desc}</span>
        <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em]" style={{ fontFamily: "Space Grotesk", color: brand.yellow }}>{L.watch} <ArrowRight size={16} /></span>
      </span>
    </span>
  </button>
);


export default function FactorySection({ onPlay }) {
  const isIT = getActiveLang() === "it";
  const L = FT[getActiveLang()] || FT.it;
  const featured = factory.documentary.find((v) => v.featured);
  const clips = factory.documentary.filter((v) => !v.featured);
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(1);
  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap() + 1);
    onSelect();
    api.on("select", onSelect);
    return () => api.off("select", onSelect);
  }, [api]);
  return (
    <section id="fabbrica" className="bg-[#F5F5F3] text-black py-24 md:py-32">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Text */}
          <div className="lg:col-span-5">
            <Reveal>
              <span className="text-black/60 text-[12px] uppercase tracking-[0.28em]" style={{ fontFamily: "Space Grotesk" }}>
                {factory.eyebrow}
              </span>
              <h2 className="mt-5 font-black leading-[1.02] tracking-tight" style={{ fontFamily: "Archivo", fontSize: "clamp(1.9rem, 3.4vw, 3.1rem)" }}>
                {factory.title}
              </h2>
            </Reveal>

            <div className="mt-10 space-y-5">
              {factory.points.map((p, i) => (
                <Reveal key={p.k} delay={0.08 * (i + 1)}>
                  <div className="flex gap-5 border-t border-black/10 pt-5">
                    <span className="font-black text-black/25 text-sm mt-1" style={{ fontFamily: "Archivo" }}>
                      0{i + 1}
                    </span>
                    <div>
                      <h3 className="font-bold text-lg" style={{ fontFamily: "Archivo" }}>{p.k}</h3>
                      <p className="text-black/60 mt-1" style={{ fontFamily: "Inter" }}>{p.v}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            {isIT && (
              <Reveal delay={0.1}>
                <Link
                  to="/pannelli-fotovoltaici-italiani"
                  data-testid="factory-pillar-link"
                  className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-black border-b-2 pb-1 transition-colors hover:text-black/60"
                  style={{ fontFamily: "Space Grotesk", borderColor: brand.yellow }}
                >
                  Scopri i pannelli fotovoltaici italiani TORRI <ArrowRight size={15} />
                </Link>
              </Reveal>
            )}
          </div>
          <div className="lg:col-span-7">
            <Reveal y={40}>
              <div className="relative w-full md:w-[80.5%] md:mx-auto">
                {/* Main video: desktop ingrandito del 15% */}
                <div className="relative overflow-hidden group w-full">
                  <LazyVideo
                    src="/img/factory.mp4"
                    mobileSrc="/img/factory-m.mp4"
                    poster="/img/factory-poster.webp"
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                    className="w-full h-[294px] md:h-[451px] object-cover bg-black"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Documentary carousel — Ingegneria Italia */}
        <div className="mt-20 pt-16 border-t border-black/10">
          <Carousel opts={{ align: "start", loop: true }} setApi={setApi}>
            <Reveal>
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
                <div className="max-w-2xl">
                  <span className="text-black/60 text-[12px] uppercase tracking-[0.28em]" style={{ fontFamily: "Space Grotesk" }}>
                    Documentario
                  </span>
                  <h3 className="mt-4 font-black leading-[1.05] tracking-tight" style={{ fontFamily: "Archivo", fontSize: "clamp(1.6rem, 3vw, 2.6rem)" }}>
                    {factory.documentaryTitle}
                  </h3>
                  <p className="mt-4 text-black/60 leading-relaxed" style={{ fontFamily: "Inter" }}>
                    {factory.documentarySub}
                  </p>
                </div>
              </div>
            </Reveal>

            {featured && <FeaturedDoc v={featured} onPlay={onPlay} L={L} />}
            <CarouselContent className="-ml-4">
              {clips.map((v) => (
                <CarouselItem key={v.id} className="pl-4 basis-[82%] sm:basis-1/2 lg:basis-1/3">
                  <button onClick={() => onPlay(v)} className="group text-left w-full">
                    <span className="relative block w-full aspect-video overflow-hidden bg-black">
                      <img decoding="async"
                        src={ytThumb(v.id)}
                        onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${v.id}/hqdefault.jpg`; }}
                        alt={v.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <span className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent group-hover:from-black/25 transition-colors" />
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="flex items-center justify-center w-14 h-14 rounded-full shadow-lg group-hover:scale-110 transition-transform" style={{ background: brand.yellow }}>
                          <Play size={20} fill="black" className="text-black ml-0.5" />
                        </span>
                      </span>
                    </span>
                    {v.phase && <span className="mt-4 inline-block px-2 py-0.5 text-[11px] uppercase tracking-[0.16em] font-bold text-black" style={{ fontFamily: "Space Grotesk", background: brand.yellow }}>{L.phase}</span>}
                    <span className="mt-3 block font-black text-black text-lg leading-snug group-hover:text-black/70 transition-colors" style={{ fontFamily: "Archivo" }}>
                      {v.title}
                    </span>
                    <span className="mt-2 block text-black/55 text-sm leading-relaxed" style={{ fontFamily: "Inter" }}>
                      {v.desc}
                    </span>
                  </button>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="mt-8 flex items-center gap-4">
              <CarouselPrevious data-testid="doc-carousel-prev" className="static translate-x-0 translate-y-0 h-11 w-11 rounded-none border-black/20 text-black hover:bg-black hover:text-white" />
              <span data-testid="doc-carousel-counter" className="min-w-[56px] text-center text-sm font-bold tracking-[0.12em] text-black" style={{ fontFamily: "Space Grotesk" }}>
                {current} / {clips.length}
              </span>
              <CarouselNext data-testid="doc-carousel-next" className="static translate-x-0 translate-y-0 h-11 w-11 rounded-none border-black/20 text-black hover:bg-black hover:text-white" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>
  );
}
